import { useWorkspace } from '@/features/workspaces/hooks';
import { SelectOption } from '@/lib/Selects/Select/types';
import {
  DEFAULT_FILTER_IDS,
  PerformanceAudienceFilterOption,
  useGetCampaignPerformanceAudiencesQuery,
  useGetCampaignPerformanceSourcesQuery,
} from '@/services/endpoints/performanceAudience';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PerformanceAudienceFiltersState } from '../types';
import { getRealAccountOptions, isAllAccountsScopeId } from '../utils/buildCampaignPerformanceDashboardArgs';
import { mapCampaignPerformanceAudiencesToFilterOptions } from '../utils/campaignPerformanceAudiences';
import { mapCampaignPerformanceSourcesToFilterOptions } from '../utils/campaignPerformanceSources';

const STATIC_DATE_RANGES: PerformanceAudienceFilterOption[] = [
  { id: 'today', value: 'performanceAudience.filters.today' },
  { id: 'yesterday', value: 'performanceAudience.filters.yesterday' },
  { id: 'last_7_days', value: 'performanceAudience.filters.last_7_days' },
  { id: 'last_14_days', value: 'performanceAudience.filters.last_14_days' },
  { id: 'last_30_days', value: 'performanceAudience.filters.last_30_days' },
  { id: 'current_month', value: 'performanceAudience.filters.current_month' },
  { id: 'last_month', value: 'performanceAudience.filters.last_month' },
  { id: 'current_quarter', value: 'performanceAudience.filters.current_quarter' },
  { id: 'custom', value: 'performanceAudience.filters.custom' },
];

const EMPTY_FILTERS: PerformanceAudienceFiltersState = {
  audienceId: '',
  googleScopeId: '',
  metaScopeIds: [],
  dateRange: DEFAULT_FILTER_IDS.dateRange,
};

const allRealMetaIds = (options: PerformanceAudienceFilterOption[]) => getRealAccountOptions(options).map((option) => option.id);

const areMetaIdsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) {
    return false;
  }
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((id, index) => id === sortedB[index]);
};

export const usePerformanceAudienceBootstrap = () => {
  const { id: workspaceId } = useWorkspace();

  const [filters, setFilters] = useState<PerformanceAudienceFiltersState>(EMPTY_FILTERS);

  const {
    data: campaignPerformanceAudiences,
    isLoading: isCampaignPerformanceAudiencesLoading,
    isError: isCampaignPerformanceAudiencesError,
    refetch: refetchCampaignPerformanceAudiences,
  } = useGetCampaignPerformanceAudiencesQuery({ workspaceId }, { skip: !workspaceId });

  const {
    data: campaignPerformanceSources,
    isLoading: isCampaignPerformanceSourcesLoading,
    isError: isCampaignPerformanceSourcesError,
    refetch: refetchCampaignPerformanceSources,
  } = useGetCampaignPerformanceSourcesQuery({ workspaceId }, { skip: !workspaceId });

  const isBootstrapSettled = !isCampaignPerformanceAudiencesLoading && !isCampaignPerformanceSourcesLoading && !!workspaceId;

  const audienceFilterOptions = useMemo(
    () => mapCampaignPerformanceAudiencesToFilterOptions(campaignPerformanceAudiences, []),
    [campaignPerformanceAudiences]
  );

  const { googleScopes: mappedGoogleScopes, metaScopes: mappedMetaScopes } = useMemo(
    () =>
      mapCampaignPerformanceSourcesToFilterOptions(campaignPerformanceSources, {
        googleScopes: [],
        metaScopes: [],
      }),
    [campaignPerformanceSources]
  );

  const hasAudiences = audienceFilterOptions.length > 0;
  const hasSources = mappedGoogleScopes.length > 0 || mappedMetaScopes.length > 0;

  const isInitialLoading = !!workspaceId && !isBootstrapSettled;

  const isFiltersReady = useMemo(() => {
    if (!isBootstrapSettled || !hasAudiences || !hasSources) {
      return false;
    }

    const audienceValid = audienceFilterOptions.some((o) => o.id === filters.audienceId);
    const googleValid = mappedGoogleScopes.length === 0 || mappedGoogleScopes.some((o) => o.id === filters.googleScopeId);

    const realMetaIds = new Set(allRealMetaIds(mappedMetaScopes));
    const metaValid =
      mappedMetaScopes.length === 0 ||
      (filters.metaScopeIds.length > 0 && filters.metaScopeIds.every((id) => realMetaIds.has(id) || isAllAccountsScopeId(id)));

    return audienceValid && googleValid && metaValid;
  }, [isBootstrapSettled, hasAudiences, hasSources, audienceFilterOptions, mappedGoogleScopes, mappedMetaScopes, filters]);

  const isError = isCampaignPerformanceAudiencesError || isCampaignPerformanceSourcesError;

  const refetch = useCallback(() => {
    void refetchCampaignPerformanceAudiences();
    void refetchCampaignPerformanceSources();
  }, [refetchCampaignPerformanceAudiences, refetchCampaignPerformanceSources]);

  useEffect(() => {
    if (!audienceFilterOptions.length) {
      return;
    }

    const hasValidSelection = audienceFilterOptions.some((option) => option.id === filters.audienceId);

    if (hasValidSelection) {
      return;
    }

    setFilters((current) => ({ ...current, audienceId: audienceFilterOptions[0].id }));
  }, [audienceFilterOptions, filters.audienceId]);

  useEffect(() => {
    if (!mappedGoogleScopes.length) {
      if (filters.googleScopeId) {
        setFilters((current) => ({ ...current, googleScopeId: '' }));
      }
      return;
    }

    const hasValidSelection = mappedGoogleScopes.some((option) => option.id === filters.googleScopeId);

    if (hasValidSelection) {
      return;
    }

    setFilters((current) => ({ ...current, googleScopeId: mappedGoogleScopes[0].id }));
  }, [mappedGoogleScopes, filters.googleScopeId]);

  useEffect(() => {
    const realMetaIds = allRealMetaIds(mappedMetaScopes);

    if (!realMetaIds.length) {
      setFilters((current) => (current.metaScopeIds.length ? { ...current, metaScopeIds: [] } : current));
      return;
    }

    const realIdSet = new Set(realMetaIds);

    setFilters((current) => {
      const nextIds = current.metaScopeIds.filter((id) => realIdSet.has(id));
      if (nextIds.length === 0) {
        return areMetaIdsEqual(current.metaScopeIds, realMetaIds) ? current : { ...current, metaScopeIds: realMetaIds };
      }
      if (!areMetaIdsEqual(nextIds, current.metaScopeIds)) {
        return { ...current, metaScopeIds: nextIds };
      }
      return current;
    });
  }, [mappedMetaScopes]);

  const setAudienceFilter = useCallback((option: SelectOption) => {
    setFilters((current) => ({ ...current, audienceId: String(option.id) }));
  }, []);

  const setGoogleScopeFilter = useCallback((option: SelectOption) => {
    setFilters((current) => ({ ...current, googleScopeId: String(option.id) }));
  }, []);

  const setMetaScopeIds = useCallback((ids: string[]) => {
    setFilters((current) => ({ ...current, metaScopeIds: ids }));
  }, []);

  const setDateRangeFilter = useCallback((option: SelectOption) => {
    if (option.id === 'custom' || option.disabled) {
      return;
    }

    setFilters((current) => ({
      ...current,
      dateRange: String(option.id),
      dateFrom: undefined,
      dateTo: undefined,
    }));
  }, []);

  const setCustomDateRange = useCallback((range: { from: string; to: string }) => {
    setFilters((current) => ({
      ...current,
      dateRange: 'custom',
      dateFrom: range.from,
      dateTo: range.to,
    }));
  }, []);

  return {
    workspaceId,
    filters,
    isFiltersReady,
    hasAudiences,
    hasSources,
    filterOptions: {
      audiences: audienceFilterOptions,
      googleScopes: mappedGoogleScopes,
      metaScopes: mappedMetaScopes,
      dateRanges: STATIC_DATE_RANGES,
    },
    setAudienceFilter,
    setGoogleScopeFilter,
    setMetaScopeIds,
    setDateRangeFilter,
    setCustomDateRange,
    isInitialLoading,
    isError,
    refetch,
  };
};
